import { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import axios from "axios";
import { ShieldCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { IssuedConfirmationView } from "@/components/bookings/IssuedConfirmationView";

// Restricted confirmation-only page: no Atlas session is created or used. Plain axios (no auth interceptors); the link token stays in the URL,
// the view token lives only in memory for this tab. Every request is re-authorised server-side.
const http = axios.create({ baseURL: `${process.env.REACT_APP_BACKEND_URL}/api`, withCredentials: false });
const msg = (e, fallback) => e.response?.data?.detail || (e.response?.data?.error) || fallback;

export default function ConfirmationAccess() {
  const { grantId } = useParams();
  const [params] = useSearchParams();
  const k = params.get("k") || "";
  const [stage, setStage] = useState("loading");
  const [info, setInfo] = useState(null);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [view, setView] = useState(null);

  useEffect(() => {
    http.get(`/confirmation-access/${grantId}`, { params: { k } }).then((r) => { setInfo(r.data); setStage("code"); }).catch((e) => { setError(msg(e, "This confirmation link is not valid")); setStage("blocked"); });
  }, [grantId, k]);

  const requestCode = async () => {
    setBusy(true); setNotice(null);
    try { const r = await http.post(`/confirmation-access/${grantId}/request-code`, { k }); setNotice(`A six-digit code has been e-mailed to ${r.data.recipient_hint}. It expires in ${r.data.expires_in_minutes} minutes and can be used once.`); setStage("verify"); }
    catch (e) { setNotice(msg(e, "The code could not be sent")); }
    finally { setBusy(false); }
  };
  const verify = async () => {
    setBusy(true); setNotice(null);
    try {
      const v = await http.post(`/confirmation-access/${grantId}/verify`, { k, code: code.trim() });
      const r = await http.get(`/confirmation-access/${grantId}/view`, { params: { k }, headers: { "X-Confirmation-View": v.data.view_token } });
      setView(r.data); setStage("view");
    } catch (e) { setNotice(msg(e, "That code could not be verified")); }
    finally { setBusy(false); }
  };

  return (
    <main className="min-h-screen bg-jigsaw-bg px-4 py-8" data-testid="confirmation-access-page">
      <div className="mx-auto max-w-2xl space-y-5">
        <header className="flex items-center gap-3"><ShieldCheck className="h-7 w-7 text-jigsaw-orange" /><div><p className="text-[11px] font-semibold uppercase tracking-[0.08em] text-jigsaw-muted">Jigsaw Conferences · Atlas</p><h1 className="text-xl font-semibold text-jigsaw-navy">Secure confirmation</h1></div></header>
        {stage === "loading" && <p className="text-sm text-jigsaw-muted" data-testid="ca-loading">Checking your link…</p>}
        {stage === "blocked" && <div className="rounded-[10px] border border-jigsaw-border bg-white p-5" data-testid="ca-blocked"><p className="text-sm font-medium text-jigsaw-navy">This link cannot be used</p><p className="mt-1 text-sm text-jigsaw-muted" data-testid="ca-blocked-detail">{error}</p></div>}
        {(stage === "code" || stage === "verify") && info && (
          <section className="space-y-4 rounded-[10px] border border-jigsaw-border bg-white p-5" data-testid="ca-gate">
            <p className="text-sm text-jigsaw-navy">Confirmation for booking <span className="font-mono-ref font-semibold">{info.booking_reference}</span>, addressed to <span className="font-medium" data-testid="ca-recipient-hint">{info.recipient_hint}</span>.</p>
            <p className="text-[13px] text-jigsaw-muted">To view it, request a six-digit code. The code is sent only to that address, lasts {info.code_minutes} minutes and works once. This link expires on {new Date(info.link_expires_at).toLocaleDateString("en-GB")} and is personal — a forwarded link will not work for anyone else.</p>
            <Button data-testid="ca-request-code" onClick={requestCode} disabled={busy} className="bg-jigsaw-orange text-white hover:bg-jigsaw-orangeDark">{stage === "verify" ? "Send me a new code" : "Send me a code"}</Button>
            {notice && <p className="text-[13px] text-jigsaw-navy" role="status" data-testid="ca-notice">{notice}</p>}
            {stage === "verify" && (
              <div className="space-y-2">
                <Label htmlFor="ca-code">Six-digit code</Label>
                <div className="flex flex-col gap-2 sm:flex-row"><Input id="ca-code" data-testid="ca-code-input" inputMode="numeric" maxLength={6} value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))} className="h-10 w-full font-mono-ref text-lg tracking-[0.3em] sm:w-48" />
                  <Button data-testid="ca-verify" onClick={verify} disabled={busy || code.length !== 6}>View confirmation</Button></div>
              </div>
            )}
          </section>
        )}
        {stage === "view" && view && (
          <>
            {view.recipient_name && <p className="text-sm text-jigsaw-muted" data-testid="ca-greeting">Prepared for {view.recipient_name}</p>}
            <IssuedConfirmationView data={view.confirmation} testId="ca-confirmation" />
            <p className="text-[12px] text-jigsaw-muted" data-testid="ca-downloads">Downloads are not available on this link. Questions? Contact your Jigsaw coordinator.</p>
          </>
        )}
      </div>
    </main>
  );
}
